import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { fetchSuperAdminUsers, toggleUserStatus, forceLogoutUser } from '../../../actioncreators/actioncreators';
import './SuperAdmin.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const SuperAdminUsers = () => {
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('created');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Re-fetch whenever the selected org changes; reset all UI state
  useEffect(() => {
    fetchAllUsers();
    setSearchTerm('');
    setStatusFilter('ALL');
    setCurrentPage(1);
  }, [superAdminSelectedOrgId]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await fetchSuperAdminUsers(superAdminSelectedOrgId || undefined);
      setUsers(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedUser) return;
    try {
      let response;
      if (actionType === 'toggle') {
        response = await toggleUserStatus(selectedUser.user_id, selectedUser.status !== 'ACTIVE');
      } else if (actionType === 'logout') {
        response = await forceLogoutUser(selectedUser.user_id);
      }
      if (response?.data?.businessStatusCode !== undefined && response.data.businessStatusCode !== 2) {
        throw new Error(response.data.message || 'Action failed');
      }
      setShowModal(false);
      fetchAllUsers();
      alert(response?.data?.message || 'Action completed successfully');
    } catch (error) {
      console.error('Action failed:', error);
      alert('Action failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const openModal = (user, type) => {
    setSelectedUser(user);
    setActionType(type);
    setShowModal(true);
  };

  const isOrgView = !!superAdminSelectedOrgId;

  // Apply search + status filter + sort; reset to page 1 whenever they change
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.mobile && user.mobile.includes(searchTerm));
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (sortBy === 'name') return a.full_name.localeCompare(b.full_name);
    if (sortBy === 'email') return a.email.localeCompare(b.email);
    return new Date(b.creation_date_and_time) - new Date(a.creation_date_and_time);
  });

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp current page when filters narrow results
  const safePage = Math.min(currentPage, totalPages);
  const pagedUsers = filteredUsers.slice((safePage - 1) * pageSize, safePage * pageSize);

  const firstRecord = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const lastRecord = Math.min(safePage * pageSize, total);

  const handleFilterChange = useCallback((setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Build sliding 5-page window around current page
  const getPageWindow = () => {
    const WING = 2;
    let start = Math.max(1, safePage - WING);
    let end = Math.min(totalPages, safePage + WING);
    if (end - start < 4) {
      if (start === 1) end = Math.min(totalPages, start + 4);
      else start = Math.max(1, end - 4);
    }
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const pageWindow = getPageWindow();
  const isFirstPage = safePage === 1;
  const isLastPage = safePage >= totalPages;

  if (loading) return (
    <div className="super-admin-users-loading">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3">Loading users...</p>
    </div>
  );

  return (
    <div className="super-admin-users">
      {isOrgView && (
        <div className="org-view-banner">
          <span className="org-view-label">
            Viewing users for: <strong>{superAdminSelectedOrgData?.org_name || 'Selected Organization'}</strong>
          </span>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or mobile..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        <div className="filter-buttons">
          {['ALL', 'ACTIVE', 'INACTIVE'].map(s => (
            <button
              key={s}
              className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => handleFilterChange(setStatusFilter)(s)}
            >
              {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Users List */}
      <div className={`users-list-container${isOrgView ? ' org-view' : ''}`}>
        {total === 0 ? (
          <div className="no-users">
            <p>No users found</p>
          </div>
        ) : (
          <>
            <div className="list-header">
              <div className="col-name">
                <button className="sort-header" onClick={() => { setSortBy('name'); setCurrentPage(1); }}>
                  Name {sortBy === 'name' && '↓'}
                </button>
              </div>
              <div className="col-email">
                <button className="sort-header" onClick={() => { setSortBy('email'); setCurrentPage(1); }}>
                  Email {sortBy === 'email' && '↓'}
                </button>
              </div>
              <div className="col-mobile">Mobile</div>
              {isOrgView && <div className="col-role">Role</div>}
              <div className="col-status">Status</div>
              <div className="col-login">Last Login</div>
              <div className="col-created">
                <button className="sort-header" onClick={() => { setSortBy('created'); setCurrentPage(1); }}>
                  Created {sortBy === 'created' && '↓'}
                </button>
              </div>
              {!isOrgView && <div className="col-actions">Actions</div>}
            </div>

            <div className="list-body">
              {pagedUsers.map((user) => (
                <div key={user.user_id} className="list-row">
                  <div className="col-name">
                    <div className="user-name-cell">
                      <span className="user-avatar-mini">{user.full_name?.charAt(0).toUpperCase()}</span>
                      <span className="user-name-text">{user.full_name}</span>
                    </div>
                  </div>
                  <div className="col-email">{user.email}</div>
                  <div className="col-mobile">{user.mobile || 'N/A'}</div>
                  {isOrgView && (
                    <div className="col-role">
                      <span className="role-label">{user.role_name || 'N/A'}</span>
                    </div>
                  )}
                  <div className="col-status">
                    <Badge className={`status-badge-list ${user.status?.toLowerCase()}`}>
                      {user.status}
                    </Badge>
                    {user.session_status === 'ACTIVE' && (
                      <Badge className="online-badge-list">🟢 Online</Badge>
                    )}
                  </div>
                  <div className="col-login">
                    {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                  </div>
                  <div className="col-created">
                    {new Date(user.creation_date_and_time).toLocaleDateString()}
                  </div>
                  {!isOrgView && (
                    <div className="col-actions">
                      <div className="action-buttons">
                        <Button
                          size="sm"
                          className={`list-action-btn ${user.status === 'ACTIVE' ? 'disable' : 'enable'}`}
                          onClick={() => openModal(user, 'toggle')}
                          title={user.status === 'ACTIVE' ? 'Disable user' : 'Enable user'}
                        >
                          {user.status === 'ACTIVE' ? '🔒' : '🔓'}
                        </Button>
                        {user.session_status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            className="list-action-btn logout"
                            onClick={() => openModal(user, 'logout')}
                            title="Force logout"
                          >
                            🚪
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Footer */}
            <div className="sau-footer">
              <div className="al-footer-left">
                <span className="al-record-summary">
                  {total === 0
                    ? 'No records'
                    : <>Showing <strong>{firstRecord}–{lastRecord}</strong> of <strong>{total}</strong> users</>
                  }
                </span>
                <div className="al-page-size">
                  <span className="al-page-size-label">Rows per page:</span>
                  <select
                    className="al-page-size-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="al-pagination">
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(1)}
                  disabled={isFirstPage}
                  title="First page"
                >
                  ««
                </button>
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(safePage - 1)}
                  disabled={isFirstPage}
                  title="Previous page"
                >
                  ‹ Prev
                </button>

                {pageWindow[0] > 1 && (
                  <>
                    <button className="al-page-btn" onClick={() => handlePageChange(1)}>1</button>
                    {pageWindow[0] > 2 && <span className="al-page-ellipsis">…</span>}
                  </>
                )}

                {pageWindow.map(n => (
                  <button
                    key={n}
                    className={`al-page-btn${n === safePage ? ' al-page-btn--active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >
                    {n}
                  </button>
                ))}

                {pageWindow.at(-1) < totalPages && (
                  <>
                    {pageWindow.at(-1) < totalPages - 1 && <span className="al-page-ellipsis">…</span>}
                    <button className="al-page-btn" onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(safePage + 1)}
                  disabled={isLastPage}
                  title="Next page"
                >
                  Next ›
                </button>
                <button
                  className="al-page-btn al-page-nav"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={isLastPage}
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal — only used in Super Admin user management (no org selected) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} className="modern-modal" centered>
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title className="modal-title-custom">
            {actionType === 'toggle' ? '⚙️ Toggle User Status' : '🚪 Force Logout'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {actionType === 'toggle' ? (
            <div className="modal-content">
              <p className="modal-message">
                Are you sure you want to <strong>{selectedUser?.status === 'ACTIVE' ? 'disable' : 'enable'}</strong> this user account?
              </p>
              <div className="user-info-modal">
                <p><strong>Name:</strong> {selectedUser?.full_name}</p>
                <p><strong>Email:</strong> {selectedUser?.email}</p>
              </div>
            </div>
          ) : (
            <div className="modal-content">
              <p className="modal-message">
                Are you sure you want to <strong>force logout</strong> this user?
              </p>
              <div className="user-info-modal">
                <p><strong>Name:</strong> {selectedUser?.full_name}</p>
                <p><strong>Email:</strong> {selectedUser?.email}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="light" onClick={() => setShowModal(false)} className="modal-btn-cancel">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAction} className="modal-btn-confirm">
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperAdminUsers;
